'use client';

import Box from '#/features/components/Box';
import {
  DATE_SEARCH_PARAM_KEY,
  END_DATE_SEARCH_PARAM_KEY,
  MONTH_SEARCH_PARAM_KEY,
  START_DATE_SEARCH_PARAM_KEY,
  useURLFilterParams,
  useUpdateQueryParams,
} from '#/utils/queryParams';
import classNames from 'classnames';
import dayjs, {type Dayjs} from 'dayjs';
import {useCallback, type ComponentProps, useId} from 'react';

import Calendar from '#/features/components/Calendar';
import Select from '#/features/components/Select';
import {Heading3, SmallText} from '#/features/components/Text';
import {useUiStrings} from '#/features/store/uiStrings';
import {useMonthDateOptions, useTimeframeDateOptions, type DateOption} from '#/utils/dates';
import {type DateRange, type SelectRangeEventHandler} from 'react-day-picker';
import {PopoverTooltip} from '#/features/components/PopoverTooltip';
import RadioInput from '#/features/components/RadioInput';

type Props = ComponentProps<typeof Box> & {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onChangeTimeRange: (label: string, startDate: Dayjs | null, endDate: Dayjs | null) => void | Promise<void>;
  unMountInputs?: boolean;
  tooltipWithEmphasis?: string;
};

const CalendarFilter = ({
  className,
  startDate,
  endDate,
  onChangeTimeRange,
  unMountInputs,
  tooltipWithEmphasis,
  ...props
}: Props) => {
  const {date, month} = useURLFilterParams();
  const updateQueryParams = useUpdateQueryParams();
  const timeframeDateOptions = useTimeframeDateOptions();
  const monthDateOptions = useMonthDateOptions();
  const uiStrings = useUiStrings();
  const currentMonth = monthDateOptions.find(option => option.id === month);
  const radioGroupName = useId();

  const handleOptionChange = useCallback(
    async (optionId: string) => {
      const option = timeframeDateOptions.find(option => option.id === optionId);
      if (!option) throw new Error(`Calendar option ${optionId} not found`);
      updateQueryParams({
        [DATE_SEARCH_PARAM_KEY]: optionId,
        [MONTH_SEARCH_PARAM_KEY]: null,
        [START_DATE_SEARCH_PARAM_KEY]: null,
        [END_DATE_SEARCH_PARAM_KEY]: null,
      });
      void onChangeTimeRange(option.title, option.startDate, option.endDate);
    },
    [timeframeDateOptions, onChangeTimeRange, updateQueryParams]
  );

  const handleMonthChange = useCallback(
    async (currentMonth: DateOption) => {
      updateQueryParams({
        [DATE_SEARCH_PARAM_KEY]: null,
        [MONTH_SEARCH_PARAM_KEY]: currentMonth.id,
        [START_DATE_SEARCH_PARAM_KEY]: currentMonth.startDate.toISOString(),
        [END_DATE_SEARCH_PARAM_KEY]: currentMonth.endDate.toISOString(),
      });
      await onChangeTimeRange(currentMonth.title, currentMonth.startDate, currentMonth.endDate);
    },
    [onChangeTimeRange, updateQueryParams]
  );

  const handleRangeSelect = useCallback<SelectRangeEventHandler>(
    newRange => {
      const newStartDate = newRange?.from ? dayjs(newRange.from).startOf('day') : null;
      const newEndDate = !!newRange?.to
        ? dayjs(newRange.to).endOf('day')
        : !!newStartDate
          ? newStartDate.endOf('day')
          : null;

      updateQueryParams({
        [DATE_SEARCH_PARAM_KEY]: null,
        [MONTH_SEARCH_PARAM_KEY]: null,
        [START_DATE_SEARCH_PARAM_KEY]: newStartDate?.toISOString() ?? null,
        [END_DATE_SEARCH_PARAM_KEY]: newEndDate?.toISOString() ?? null,
      });
      void onChangeTimeRange('Custom', newStartDate, newEndDate);
    },
    [onChangeTimeRange, updateQueryParams]
  );

  const range: DateRange = {from: startDate?.toDate(), to: endDate?.toDate()};

  return (
    <Box className={classNames('gap-y-2', className)} {...props}>
      <div className="flex flex-row items-center justify-between gap-x-2">
        <Heading3 className="text-lg">{uiStrings.events.calendar}</Heading3>
        {!!tooltipWithEmphasis && <PopoverTooltip tooltipWithEmphasis={tooltipWithEmphasis} />}
      </div>
      <div className="flex flex-col gap-3">
        <div className="ms-2 mt-1 grid grid-cols-2 gap-y-1">
          {timeframeDateOptions.map(option => (
            <div
              className="flex flex-row items-center gap-x-2"
              onClick={() => handleOptionChange(option.id)}
              key={option.id}
            >
              {unMountInputs && <p id={`${option.id}-${unMountInputs}`}>{option.id}</p>}
              {!unMountInputs && (
                <>
                  <RadioInput
                    type="radio"
                    id={`${option.id}-${unMountInputs}`}
                    checked={option.id === date}
                    name={radioGroupName}
                  />
                  <label htmlFor={`${option.id}-${unMountInputs}`}>
                    <SmallText>{option.title}</SmallText>
                  </label>
                </>
              )}
            </div>
          ))}
        </div>
        <Select
          value={currentMonth}
          onChange={handleMonthChange}
          title={currentMonth?.title ?? uiStrings.events.selectMonth}
          options={monthDateOptions}
        />
        <Calendar range={range} onRangeSelect={handleRangeSelect} />
      </div>
    </Box>
  );
};

export default CalendarFilter;
